import type { DatentransformationDebugSettings } from '@core/domain';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import {
  useDatentransformationDebugSettings,
  useUpdateDatentransformationDebugSettings,
} from '@/adapter/opm-cockpit-backend/useDatentransformationDebugSettings';
import { Checkbox } from '@/components/ui/checkbox';
import { ActionButton } from '@/pages/VorgangBearbeitung/components/ActionButton';

const debugOptions = [
  {
    key: 'aktuelleEmailadresseVerwenden',
    label: 'Aktuelle E-Mailadresse verwenden',
  },
  {
    key: 'aktuelleVersandartVerwenden',
    label: 'Aktuelle Versandart verwenden',
  },
] as const;

export const DebugConfig: React.FC = () => {
  const { data, isLoading, error } = useDatentransformationDebugSettings();
  const mutation = useUpdateDatentransformationDebugSettings();
  const [draft, setDraft] = useState<DatentransformationDebugSettings | null>(
    null,
  );
  if (isLoading) return <p>Lade Debug-Einstellungen...</p>;
  if (error || !data)
    return (
      <p role="alert" className="text-destructive">
        Debug-Einstellungen konnten nicht geladen werden.
      </p>
    );
  const settings = draft ?? data;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (mutation.isPending) return;
        mutation.mutate(settings, { onSuccess: () => setDraft(null) });
      }}
    >
      <section
        aria-labelledby="debug-actions-heading"
        className="bg-white border border-table-border rounded-md p-4"
      >
        <div className="flex items-center justify-between gap-4">
          <h2
            id="debug-actions-heading"
            className="text-lg font-semibold text-text-primary"
          >
            Aktionen
          </h2>
          <ActionButton
            type="submit"
            loading={mutation.isPending}
            loadingText="Speichern"
            normalText="Speichern"
            icon={Check}
          />
        </div>
        {mutation.isError && (
          <p role="alert" className="text-destructive mt-3">
            Debug-Einstellungen konnten nicht gespeichert werden.
          </p>
        )}
      </section>
      <section
        aria-labelledby="debug-transformation-heading"
        className="bg-white border border-table-border rounded-md p-4"
      >
        <h2
          id="debug-transformation-heading"
          className="text-lg font-semibold text-text-primary mb-4"
        >
          Datentransformation
        </h2>
        <div className="flex flex-col gap-4">
          {debugOptions.map(({ key, label }) => (
            <label key={key} className="inline-flex items-center gap-2 text-sm">
              <Checkbox
                checked={settings[key]}
                disabled={mutation.isPending}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({
                    ...(current ?? data),
                    [key]: checked === true,
                  }))
                }
              />
              {label}
            </label>
          ))}
        </div>
      </section>
    </form>
  );
};
